import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import BlogAPI from "../api/blog";
import SEO from "../components/SEO";
import BlogCard from "../components/blog/BlogCard";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export default function BlogCategory() {
  const { categorySlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCategoryPosts();
  }, [categorySlug, page]);

  const fetchCategoryPosts = async () => {
    try {
      setIsLoading(true);
      const data = await BlogAPI.getPostsByCategory(categorySlug, { page, limit: 12 });
      setPosts(data.results || []);
      setCategory(data.category || null);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching category posts:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <SEO
        title={`${category?.name || 'Category'} - Pharmacy Wiki Blog`}
        description={category?.description || `Browse articles in the ${category?.name} category`}
        keywords={`pharmacy, ${category?.name}, healthcare, medications`}
      />

      <div className="container mx-auto px-6 mt-[90px] max-md:mt-[40px]">
        {/* Breadcrumb */}
        <nav className="mb-6 text-14 text-light-gray">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          {category && (
            <>
              <span className="mx-2">/</span>
              <span className="text-secondary">{category.name}</span>
            </>
          )}
        </nav>

        {/* Category Header */}
        <div className="text-center mb-12">
          <h1 className="font-medium text-48 text-secondary max-md:text-36 mb-4">
            {category?.name || 'Category'}
          </h1>
          {category?.description && (
            <p className="text-18 text-light-gray/80 max-md:text-14 max-w-2xl mx-auto">
              {category.description}
            </p>
          )}
        </div>

        {/* Posts Grid */}
        <div className="mt-8">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg overflow-hidden border border-stroke">
                  <Skeleton height={200} />
                  <div className="p-6">
                    <Skeleton count={1} height={24} className="mb-2" />
                    <Skeleton count={3} />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <BlogCard key={post._id} post={post} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-12">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg bg-white border border-stroke text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-secondary">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg bg-white border border-stroke text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <p className="text-light-gray text-18">No posts found in this category</p>
              <Link to="/blog" className="text-primary hover:underline mt-4 inline-block">
                View all posts
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
